import { Resource } from '../../api/authz';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    BoundItem,
    GroupSchemaClient,
    SchemaGroup,
    parseGroupFields,
} from '../../api/schema';
import { actions, useCan } from '../../store';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import BindingList from '../../components/BindingList';
import FieldGroupEditorModal, {
    FieldGroupEditorState,
    fieldGroupEditorFrom,
    newFieldGroupEditor,
} from '../../components/schema/FieldGroupEditorModal';
import Modal from '../../components/Modal';
import HelpTip from '../../components/HelpTip';
import { useCategoryTree } from '../../components/MaterialFieldInputs';
import { DictCategory, listDictCategories } from '../../api/dict';
import { ProcessArch, listProcessArchs } from '../../api/parameter';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import SearchSelect from '../../components/SearchSelect';

// 通用「字段组」型 Schema 管理器 (参-04 统一 Schema 管理).
// 材料理化 (phys_schema) 与 设备 (device_schema) 复用同一组件, 仅传入不同 client / 文案.
// 字段组 = { groupKey(业务键) + label + fields[] + 排序 + 启用 }, 值按组存 (材料按 xcode, 设备按 deviceKey).

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}


export interface FieldGroupSchemaManagerProps {
    /** RBAC 资源域 (phys_schema / device_schema): 写按钮按其 create/update/delete 权隐藏 (2026-09-12) */
    resource: Resource;
    client: GroupSchemaClient;
    /** 该类 Schema 的名称, 如 "材料理化" / "设备" */
    domainLabel: string;
    /** 值挂在谁身上的说明, 如 "按材料 (xcode) 存值" */
    valueHint: string;
    /** 按材料类别限定作用域 (仅材料理化组开启) */
    categoryScope?: boolean;
    /** 支持矩阵布局 (材料理化 + 设备组均开启) */
    matrixLayout?: boolean;
    /** 按工艺框架分级作用域 (仅设备组开启): 通用 → 框架 → 加工类型 */
    archScope?: boolean;
    /** 定位模板编码 (?focus= 跳转进来时): 加载后滚动到该模板区块并短暂高亮 */
    focusTemplate?: string;
}

function FieldGroupSchemaManager({
    client,
    resource,
    domainLabel,
    valueHint,
    categoryScope,
    matrixLayout,
    archScope,
    focusTemplate,
}: FieldGroupSchemaManagerProps) {
    const canCreate = useCan(resource, 'create');
    const canUpdate = useCan(resource, 'update');
    const canDelete = useCan(resource, 'delete');
    const history = useHistory();
    const dispatch = useDispatch();
    const [groups, setGroups] = useState<SchemaGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editor, setEditor] = useState<FieldGroupEditorState | null>(null);
    // 绑定/关联清单模态: 点击"存值"/"关联"徽标打开, 列表可跳转
    const [listModal, setListModal] = useState<{
        title: string;
        subtitle: string;
        unit: string;
        items: BoundItem[];
    } | null>(null);
    // 跳转定位高亮的模板编码 (2.5s 后消退)
    const [flashTpl, setFlashTpl] = useState('');
    // 模板筛选 (下拉) + 关键字 (组名/组编码/字段名); 从"引用模板"跳转进来时预选该模板
    const [tplFilter, setTplFilter] = useState(focusTemplate || '');
    const [kw, setKw] = useState('');

    useEffect(() => {
        if (!focusTemplate || loading) return undefined;
        const el = document.getElementById(`schema-tpl-${focusTemplate}`);
        if (!el) return undefined;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setFlashTpl(focusTemplate);
        const t = setTimeout(() => setFlashTpl(''), 2500);
        return () => clearTimeout(t);
    }, [focusTemplate, loading]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setGroups(await client.list());
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [client]);

    useEffect(() => {
        load();
    }, [load]);

    // 字典类目 (矩阵行来源枚举下拉 + 徽标中文名); 类目在字典维护里自助新增.
    // 特殊行来源 mm_group = 加工模式组 (设备"模式能力"等矩阵用).
    const [enumCats, setEnumCats] = useState<DictCategory[]>([]);
    useEffect(() => {
        if (!matrixLayout) return;
        listDictCategories()
            .then(setEnumCats)
            .catch(() => setEnumCats([]));
    }, [matrixLayout]);
    const enumLabel = useCallback(
        (code: string) =>
            code === 'mm_group'
                ? '加工模式'
                : enumCats.find((c) => c.code === code)?.label || code,
        [enumCats],
    );

    // 工艺框架作用域候选 (设备组): 组只按框架归属 —
    // 加工类型维度已移除 (设备覆盖其框架全部类别, 无设备侧"支持哪些类别"的声明口, 过滤条件无从写起)
    const [archs, setArchs] = useState<ProcessArch[]>([]);
    useEffect(() => {
        if (!archScope) return;
        listProcessArchs()
            .then(setArchs)
            .catch(() => setArchs([]));
    }, [archScope]);
    const archLabel = useCallback(
        (code: string) => archs.find((a) => a.code === code)?.label || code,
        [archs],
    );


    // 类别树: 作用域下拉 + 卡片上类别编码 → 中文名
    const categoryTree = useCategoryTree();
    const catLabel = useCallback(
        (en: string) => {
            for (const t of categoryTree) {
                if (t.categoryCode === en) return t.nameZh || en;
                const hit = t.children?.find((c) => c.categoryCode === en);
                if (hit) return hit.nameZh || en;
            }
            return en;
        },
        [categoryTree],
    );
    const scopeLabel = (g: SchemaGroup) =>
        g.cat1 ? catLabel(g.cat1) : '全部材料';
    // 组的二级类别绑定清单 (旧单值 cat2 兜底并入)
    const groupCat2Keys = (g: SchemaGroup): string[] =>
        g.cat2Keys?.length ? g.cat2Keys : g.cat2 ? [g.cat2] : [];

    // 新建/编辑字段组: 表单与保存逻辑在 FieldGroupEditorModal (向导里同一份表单)
    const openNew = (scope?: { archCode?: string; cat1?: string }) =>
        setEditor(newFieldGroupEditor(scope));
    const openEdit = (g: SchemaGroup) => setEditor(fieldGroupEditorFrom(g, !!archScope));


    const unit = archScope ? '设备' : '材料';

    // 清单项点击: 跳转到对应对象详情. 材料走路由 /materials/:xcode; 设备无独立详情路由,
    // 经 Redux 选中 deviceKey 后跳设备工况页 (顶部选择器据此定位).
    const goItem = (code: string) => {
        setListModal(null);
        if (archScope) {
            dispatch(actions.device.setCur(code));
            history.push('/devices');
        } else {
            history.push(`/materials/${encodeURIComponent(code)}`);
        }
    };

    // 绑定清单文案 (删除阻止提示用): 最多列 20 条, 超出折叠
    const formatBindings = (
        label: string,
        total: number,
        items: BoundItem[],
    ) => {
        const MAX_SHOWN = 20;
        const shown = items
            .slice(0, MAX_SHOWN)
            .map((it) =>
                it.name && it.name !== it.code
                    ? `${it.name}(${it.code})`
                    : it.code,
            )
            .join('\n');
        const more = total > MAX_SHOWN ? `\n… 等共 ${total} 条` : '';
        return `字段组「${label}」共 ${total} 条数据绑定:\n\n${shown}${more}`;
    };

    // 模板行「关联 N」徽标点击: 弹出该框架/类别下的设备/材料清单 (可跳转)
    const showScope = async (code: string, label: string) => {
        try {
            const r = await client.scope(code);
            setListModal({
                title: `模板「${label}」· 关联${unit}`,
                subtitle: `共 ${r.total} ${archScope ? '台设备' : '个材料'} · 点击任一项跳转详情`,
                unit,
                items: r.items,
            });
        } catch (e) {
            alertDialog(`查询关联失败: ${errMsg(e)}`);
        }
    };

    // 卡片「存值 N」徽标点击: 弹出绑定对象清单 (可跳转)
    const showBindings = async (g: SchemaGroup) => {
        try {
            const bound = await client.bindings(
                g.groupKey,
                archScope ? g.archCode || '' : g.cat1 || '',
            );
            setListModal({
                title: `字段组「${g.label}」· 存值清单`,
                subtitle: `共 ${bound.total} ${archScope ? '台设备' : '个材料'}存有本组数据 · 点击任一项跳转详情`,
                unit,
                items: bound.items,
            });
        } catch (e) {
            alertDialog(`查询绑定失败: ${errMsg(e)}`);
        }
    };


    const remove = async (g: SchemaGroup) => {
        const scopeCode = archScope ? g.archCode || '' : g.cat1 || '';
        // 删除前先查绑定清单: 有绑定直接弹清单并阻止 (后端删除保护也会拒绝), 让用户看清哪些数据占用;
        // 查询失败不拦 (交给后端删除保护兜底), 无绑定才进入确认.
        try {
            const bound = await client.bindings(g.groupKey, scopeCode);
            if (bound.total > 0) {
                alertDialog(
                    `${formatBindings(g.label, bound.total, bound.items)}\n\n不能删除, 请先清空或删除这些数据后再删组.`,
                );
                return;
            }
        } catch {
            // 查询失败继续走确认 + 删除, 后端删除保护兜底
        }
        if (
            !(await confirmDialog(
                `确认删除字段组「${g.label}」? 当前没有数据绑定该组.`,
            ))
        )
            return;
        try {
            const res = await client.remove(g.groupKey, scopeCode);
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`删除失败: ${errMsg(e)}`);
        }
    };


    // ---- 模板视图 (Schema 显式绑定: 一大类一模板, 组归属模板) ----
    // 模板清单 = 全部大类 (设备: 工艺框架 / 材料: 一级类别), 没有组的模板也展示 (空模板可从这里起步);
    // 归属为空的旧"通用"组单独放"待迁移"区块.
    const templates: { code: string; label: string }[] = archScope
        ? archs.map((a) => ({
              code: a.code,
              label: `${a.icon || ''} ${a.label}`.trim(),
          }))
        : categoryTree.map((t) => ({
              code: t.categoryCode,
              label: t.nameZh || t.nameEn,
          }));
    // 关键字命中: 组名 / 组编码 / 任一字段名
    const kwHit = (g: SchemaGroup) => {
        const q = kw.trim().toLowerCase();
        if (!q) return true;
        if (
            g.label.toLowerCase().includes(q) ||
            g.groupKey.toLowerCase().includes(q)
        )
            return true;
        return parseGroupFields(g.fields).some(
            (f) =>
                f.label.toLowerCase().includes(q) ||
                f.key.toLowerCase().includes(q),
        );
    };
    const groupsOf = (code: string) =>
        groups.filter(
            (g) => (archScope ? g.archCode : g.cat1) === code && kwHit(g),
        );
    const orphanGroups = groups.filter(
        (g) => !(archScope ? g.archCode : g.cat1) && kwHit(g),
    );
    const shownTemplates = templates.filter(
        (t) => !tplFilter || t.code === tplFilter,
    );

    const renderGroupCard = (g: SchemaGroup) => {
        const fs = parseGroupFields(g.fields);
        return (
            <div
                key={g.id}
                className="card p-4 hover:shadow-md transition-shadow"
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">
                                {g.label}
                            </span>
                            {categoryScope && (
                                <span
                                    className={`tag text-[10px] ${g.cat1 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}
                                    title="类别作用域: 只有该类别的材料显示此字段组"
                                >
                                    {scopeLabel(g)}
                                </span>
                            )}
                            {/* 组内二级类别绑定: 空 = 类别全部材料; 非空 = 只有清单内二级类别的材料显示本组 */}
                            {categoryScope && (
                                <span
                                    className={`tag text-[10px] ${
                                        groupCat2Keys(g).length
                                            ? 'bg-violet-50 text-violet-600'
                                            : 'bg-slate-100 text-slate-500'
                                    }`}
                                    title={
                                        groupCat2Keys(g).length
                                            ? `只有这些二级类别的材料显示本组:\n${groupCat2Keys(
                                                  g,
                                              )
                                                  .map((k) => catLabel(k))
                                                  .join('\n')}`
                                            : '该类别的全部材料都显示本组 (未限定二级类别)'
                                    }
                                >
                                    {groupCat2Keys(g).length
                                        ? `限 ${groupCat2Keys(g).length} 个二级类别`
                                        : '全部二级类别'}
                                </span>
                            )}
                            {archScope && (
                                <span
                                    className={`tag text-[10px] ${g.archCode ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}
                                    title="框架作用域: 该框架的所有设备适用此字段组"
                                >
                                    {g.archCode
                                        ? archLabel(g.archCode)
                                        : '通用'}
                                </span>
                            )}
                            {/* 组内设备绑定: 空 = 框架全部设备; 非空 = 只有清单内设备显示本组 */}
                            {archScope && (
                                <span
                                    className={`tag text-[10px] ${
                                        g.deviceKeys?.length
                                            ? 'bg-violet-50 text-violet-600'
                                            : 'bg-slate-100 text-slate-500'
                                    }`}
                                    title={
                                        g.deviceKeys?.length
                                            ? `只有这些设备显示本组:\n${g.deviceKeys.join('\n')}`
                                            : '该框架的全部设备都显示本组 (未限定设备)'
                                    }
                                >
                                    {g.deviceKeys?.length
                                        ? `限 ${g.deviceKeys.length} 台设备`
                                        : '全部设备'}
                                </span>
                            )}
                            {g.layout === 'matrix' && (
                                <span
                                    className="tag bg-indigo-50 text-indigo-500 text-[10px]"
                                    title={`矩阵布局: 行来自枚举「${enumLabel(g.rowSource || '')}」× 下方指标列`}
                                >
                                    矩阵 × {enumLabel(g.rowSource || '')}
                                </span>
                            )}
                            {!g.isEnabled && (
                                <span className="tag bg-slate-100 text-slate-400 text-[10px]">
                                    停用
                                </span>
                            )}
                            {/* 存值数: 该组已录入数据的设备/材料数 (关联总数在模板标题行统一显示); >0 时组不可删, 点击看清单 */}
                            <button
                                type="button"
                                className={`tag text-[10px] ${
                                    (g.boundCount ?? 0) > 0
                                        ? 'bg-amber-50 text-amber-600 hover:bg-amber-100 cursor-pointer'
                                        : 'bg-slate-50 text-slate-400'
                                }`}
                                title={
                                    (g.boundCount ?? 0) > 0
                                        ? `${g.boundCount} ${archScope ? '台设备' : '个材料'}存有本组数据 (组不可删) · 点击查看清单`
                                        : `没有${archScope ? '设备' : '材料'}存有本组数据, 组可删除`
                                }
                                disabled={(g.boundCount ?? 0) === 0}
                                onClick={() => showBindings(g)}
                            >
                                存值 {g.boundCount ?? 0}
                            </button>
                        </div>
                        <div className="font-mono text-[11px] text-slate-400 mt-0.5">
                            {g.groupKey}
                            {g.src && (
                                <span className="font-sans">
                                    {' '}
                                    · 来源 {g.src}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-1 shrink-0 text-xs">
                        <button
                            type="button"
                            className="px-2 py-1 rounded-md text-brand-600 hover:bg-brand-50"
                            onClick={() => openEdit(g)}
                            hidden={!canUpdate}
                        >
                            编辑
                        </button>
                        <button
                            type="button"
                            className="px-2 py-1 rounded-md text-rose-500 hover:bg-rose-50"
                            onClick={() => remove(g)}
                            hidden={!canDelete}
                        >
                            删除
                        </button>
                    </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-1.5">
                    {fs.map((f) => (
                        <span
                            key={f.key}
                            className="inline-flex items-baseline gap-1 px-2 py-1 rounded-md bg-slate-50 border border-slate-100 text-xs text-slate-600"
                            title={
                                f.options?.length
                                    ? `${f.key} · 枚举: ${f.options.join(' / ')}`
                                    : f.key
                            }
                        >
                            {f.label}
                            {f.unit && (
                                <span className="text-[10px] text-slate-400">
                                    {f.unit}
                                </span>
                            )}
                            {!!f.options?.length && (
                                <span className="text-[10px] text-indigo-400">
                                    枚举{f.options.length}
                                </span>
                            )}
                        </span>
                    ))}
                    {fs.length === 0 && (
                        <span className="text-[11px] text-slate-300">
                            无字段
                        </span>
                    )}
                </div>
                <div className="mt-2 text-[11px] text-slate-400">
                    {fs.length} 个字段
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* 筛选: 模板下拉 + 关键字 (组名/组编码/字段名); 页面说明收进 ? 气泡 */}
            <div className="flex flex-wrap items-center gap-2">
                <SearchSelect
                    className="w-52"
                    value={tplFilter}
                    onChange={(v) => setTplFilter(v)}
                    placeholder={`全部模板 (${templates.length})`}
                    options={templates.map((t) => ({
                        value: t.code,
                        label:
                            templates.filter((x) => x.label === t.label)
                                .length > 1
                                ? `${t.label} (${t.code})`
                                : t.label || t.code,
                    }))}
                    clearable
                />
                <input
                    className="inp text-sm w-64"
                    value={kw}
                    onChange={(e) => setKw(e.target.value)}
                    placeholder="搜索字段组 / 字段名…"
                />
                {(tplFilter || kw) && (
                    <button
                        type="button"
                        className="text-xs text-slate-400 hover:text-slate-600"
                        onClick={() => {
                            setTplFilter('');
                            setKw('');
                        }}
                    >
                        清除筛选
                    </button>
                )}
                <HelpTip
                    text={`${domainLabel} Schema · 一大类一模板: 每个${archScope ? '工艺框架' : '一级类别'}一个字段模板, 数据行绑定模板渲染 · ${valueHint}。管理员改动即时生效, 普通用户提交走审批工单。`}
                />
                {/* 模板随大类自动存在: 新增模板 = 新增大类 (材料→材料类别管理; 设备→字典维护·工艺架构) */}
                <button
                    type="button"
                    className="ml-auto btn-ghost text-xs"
                    title={
                        archScope
                            ? '设备模板 = 工艺框架, 去「字典维护 → 工艺架构」新建, 建完本页自动出现空模板'
                            : '材料模板 = 一级类别, 去「材料类别管理」新建, 建完本页自动出现空模板'
                    }
                    onClick={() =>
                        history.push(
                            archScope
                                ? '/admin/data-dict?d=dict&tab=process_arch'
                                : '/admin/data-dict?d=category',
                        )
                    }
                    hidden={!canCreate}
                >
                    + 新增模板 ↗
                </button>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            {loading ? (
                <div className="text-center text-slate-400 text-sm py-8">
                    加载中…
                </div>
            ) : (
                <div className="space-y-5">
                    {shownTemplates.map((t) => {
                        const gs = groupsOf(t.code);
                        if (kw.trim() && gs.length === 0) return null; // 关键字搜索时隐藏无命中的模板
                        const nFields = gs.reduce(
                            (n, g) => n + parseGroupFields(g.fields).length,
                            0,
                        );
                        return (
                            <div
                                key={t.code}
                                id={`schema-tpl-${t.code}`}
                                className={`card p-4 transition-shadow ${
                                    flashTpl === t.code
                                        ? 'ring-2 ring-brand-400 shadow-lg'
                                        : ''
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-sm font-semibold">
                                        模板 · {t.label}
                                    </span>
                                    <span className="tag bg-brand-50 text-brand-700 font-mono text-[10px]">
                                        {t.code}
                                    </span>
                                    <span className="text-[11px] text-slate-400">
                                        {gs.length} 个字段组 · {nFields} 个字段
                                    </span>
                                    {/* 关联数模板级共享: 该框架/类别下在用的设备/材料总数, 全部适用本模板字段组 (不论是否已录数据); 点击看清单 */}
                                    {gs.length > 0 && (
                                        <button
                                            type="button"
                                            className={`tag text-[10px] ${
                                                (gs[0].scopeCount ?? 0) > 0
                                                    ? 'bg-sky-50 text-sky-600 hover:bg-sky-100 cursor-pointer'
                                                    : 'bg-slate-50 text-slate-400'
                                            }`}
                                            title={`该模板关联 ${gs[0].scopeCount ?? 0} ${archScope ? '台该框架设备' : '个该类别材料'}: 它们全部适用本模板的字段组 (不论是否已录入数据) · 点击查看清单`}
                                            disabled={
                                                (gs[0].scopeCount ?? 0) === 0
                                            }
                                            onClick={() =>
                                                showScope(t.code, t.label)
                                            }
                                        >
                                            关联 {gs[0].scopeCount ?? 0}{' '}
                                            {archScope ? '台设备' : '个材料'}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="ml-auto text-xs text-brand-600 hover:underline"
                                        onClick={() =>
                                            openNew(
                                                archScope
                                                    ? { archCode: t.code }
                                                    : { cat1: t.code },
                                            )
                                        }
                                        hidden={!canCreate}
                                    >
                                        + 新建字段组
                                    </button>
                                </div>
                                {gs.length === 0 ? (
                                    <div className="text-xs text-slate-300 py-3 text-center">
                                        模板为空 — 该
                                        {archScope ? '框架' : '类别'}
                                        的数据暂无动态字段, 点右上新建
                                    </div>
                                ) : (
                                    <div className="grid md:grid-cols-2 gap-3 items-start">
                                        {gs.map(renderGroupCard)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {orphanGroups.length > 0 && (
                        <div className="card p-4 border-amber-200 bg-amber-50/40">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-sm font-semibold text-amber-700">
                                    未归属模板 (旧通用组, 待迁移)
                                </span>
                                <span className="text-[11px] text-amber-600">
                                    新架构下组必须归属模板, 这些组不会再渲染 —
                                    请编辑指定{archScope ? '框架' : '类别'}
                                    或执行迁移脚本
                                </span>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3 items-start">
                                {orphanGroups.map(renderGroupCard)}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {editor && (
                <FieldGroupEditorModal
                    editor={editor}
                    setEditor={setEditor}
                    onClose={() => setEditor(null)}
                    onSaved={() => {
                        setEditor(null);
                        load();
                    }}
                    client={client}
                    domainLabel={domainLabel}
                    categoryScope={categoryScope}
                    matrixLayout={matrixLayout}
                    archScope={archScope}
                />
            )}

            {listModal && (
                <Modal
                    open
                    title={listModal.title}
                    subtitle={listModal.subtitle}
                    onClose={() => setListModal(null)}
                    width={560}
                >
                    <BindingList
                        items={listModal.items}
                        unit={listModal.unit}
                        onPick={goItem}
                    />
                </Modal>
            )}
        </div>
    );
}

export default FieldGroupSchemaManager;
